import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
    FaReact, FaNodeJs, FaPython, FaDatabase, FaAws, FaGithub,
    FaExternalLinkAlt
} from "react-icons/fa";
import {
    SiDjango, SiJavascript, SiTensorflow, SiMongodb, SiTailwindcss,
    SiKubernetes, SiDocker, SiRaspberrypi, SiArduino
} from "react-icons/si";

const SkillCard = ({ skill }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = (e) => {
        // Detect if the user is on a mobile device
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (!isMobile) {
            e.preventDefault();
            window.open(skill.url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <a
            href={skill.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`group relative flex flex-col items-center justify-center p-4 rounded-lg 
                      bg-gray-50 dark:bg-gray-700 transition-all duration-300 ease-in-out
                      hover:shadow-xl hover:transform hover:scale-105 hover:bg-blue-50 
                      dark:hover:bg-gray-600 cursor-pointer border border-transparent 
                      hover:border-blue-200 dark:hover:border-gray-500`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick} // ✅ Updated for mobile compatibility
        >
            <div className={`text-4xl transition-transform duration-300 ${isHovered ? "scale-110" : ""}`}>
                {skill.icon}
            </div>
            <p className="mt-2 text-gray-800 dark:text-white font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400">
                {skill.name}
            </p>
            <FaExternalLinkAlt
                className={`absolute top-2 right-2 text-gray-400 transition-opacity duration-300 
                           ${isHovered ? "opacity-100" : "opacity-0"}`}
                size={12}
            />
        </a>
    );
};

SkillCard.propTypes = {
    skill: PropTypes.shape({
        name: PropTypes.string.isRequired,
        icon: PropTypes.element.isRequired,
        url: PropTypes.string.isRequired,
    }).isRequired,
};

const SkillSection = ({ section }) => {
    return (
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center justify-center">
                {section.category}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {section.skills.map((skill, idx) => (
                    <SkillCard key={idx} skill={skill} />
                ))}
            </div>
        </div>
    );
};

SkillSection.propTypes = {
    section: PropTypes.shape({
        category: PropTypes.string.isRequired,
        skills: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string.isRequired,
                icon: PropTypes.element.isRequired,
                url: PropTypes.string.isRequired,
            })
        ).isRequired,
    }).isRequired,
};

const Skills = () => {
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    const skills = [
        {
            category: "Web Development",
            skills: [
                { name: "React.js", icon: <FaReact className="text-blue-500" />, url: "https://react.dev/" },
                { name: "Django", icon: <SiDjango className="text-green-500" />, url: "https://docs.djangoproject.com/en/stable/" },
                { name: "JavaScript", icon: <SiJavascript className="text-yellow-500" />, url: "https://developer.mozilla.org/en-US/docs/Web/JavaScript" },
                { name: "Node.js", icon: <FaNodeJs className="text-green-600" />, url: "https://nodejs.org/en/docs/" },
                { name: "Tailwind CSS", icon: <SiTailwindcss className="text-teal-400" />, url: "https://tailwindcss.com/docs" },
            ],
        },
        {
            category: "AI & Machine Learning",
            skills: [
                { name: "Python", icon: <FaPython className="text-blue-600" />, url: "https://docs.python.org/3/" },
                { name: "TensorFlow", icon: <SiTensorflow className="text-orange-500" />, url: "https://www.tensorflow.org/learn" },
                { name: "Deep Learning", icon: <FaDatabase className="text-purple-500" />, url: "https://www.deeplearning.ai/" },
            ],
        },
        {
            category: "IoT & Embedded Systems",
            skills: [
                { name: "Raspberry Pi", icon: <SiRaspberrypi className="text-red-500" />, url: "https://www.raspberrypi.com/documentation/" },
                { name: "Arduino", icon: <SiArduino className="text-blue-700" />, url: "https://www.arduino.cc/reference/en/" },
            ],
        },
        {
            category: "Cloud & DevOps",
            skills: [
                { name: "AWS", icon: <FaAws className="text-yellow-500" />, url: "https://aws.amazon.com/documentation/" },
                { name: "Docker", icon: <SiDocker className="text-blue-500" />, url: "https://docs.docker.com/" },
                { name: "Kubernetes", icon: <SiKubernetes className="text-blue-600" />, url: "https://kubernetes.io/docs/" },
                { name: "GitHub", icon: <FaGithub className="text-gray-600 dark:text-white" />, url: "https://docs.github.com/en" },
            ],
        },
        {
            category: "Databases",
            skills: [
                { name: "PostgreSQL", icon: <FaDatabase className="text-blue-600" />, url: "https://www.postgresql.org/docs/" },
                { name: "MySQL", icon: <FaDatabase className="text-orange-500" />, url: "https://dev.mysql.com/doc/" },
                { name: "SQLite", icon: <FaDatabase className="text-gray-600" />, url: "https://www.sqlite.org/docs.html" },
                { name: "TimescaleDB", icon: <FaDatabase className="text-green-500" />, url: "https://docs.timescale.com/" },
                { name: "MongoDB", icon: <SiMongodb className="text-green-600" />, url: "https://www.mongodb.com/docs/" }
            ],
        },

    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <section
            id="skills"
            ref={sectionRef}
            className={`py-16 bg-gray-100 dark:bg-gray-900 transition-all ${isVisible ? "animate-fadeIn" : "opacity-0"
                }`}
        >
            <div className="container mx-auto px-6 md:px-12 lg:px-20 text-center">
                <h2 className="text-4xl font-extrabold text-gray-800 dark:text-white mb-6">
                    My Skills
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12">
                    I specialize in <strong>Full-Stack Development</strong>,{" "}
                    <strong>Artificial Intelligence</strong>, <strong>IoT</strong>, and{" "}
                    <strong>Cloud Technologies</strong>. Below are my key skills:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-8">
                    {skills.map((section, index) => (
                        <SkillSection key={index} section={section} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Skills;
